import {shallow} from 'zustand/shallow';
import {useIntl} from 'react-intl';
import {BoxProps, Center, Avatar, Text, Flex, Box} from '@chakra-ui/react';
import {AddIcon} from '@chakra-ui/icons';

import {useTranslate} from '@eksab/i18n';
import {ArrowDownIcon, TShirtIcon} from '@eksab/assets/icons';

import {PlayerWarningIndicator} from './PlayerWarningIndicator';
import {useLineupStore, getIndexFromPosition} from './useLineupStore';
import {CaptainIndicator} from '../components';
import type {Player, Position} from '../types';

type PlayerSpotProps = {
  isSubstitute?: boolean;
  index: number;
  position: Position;
  isEditable?: boolean;
} & Omit<BoxProps, 'position'>;

export const EditablePlayerSpot = ({index, position, isSubstitute, isEditable = true, ...props}: PlayerSpotProps) => {
  const format = useIntl().formatNumber;

  const t = useTranslate();
  const player = useLineupStore((state) =>
    isSubstitute ? state.substitutes[index] : state.players[getIndexFromPosition(position)][index],
  );
  const selected = useLineupStore((state) =>
    isSubstitute
      ? state.indicatorPosition.substitute && state.indicatorPosition.position === position
      : !state.indicatorPosition.substitute &&
        state.indicatorPosition.position === position &&
        state.indicatorPosition.index === index,
  );
  const {setPlayerStatsModal, setIndicatorPosition, openDrawer} = useLineupStore(
    (state) => ({
      setPlayerStatsModal: state.setPlayerStatsModal,
      setIndicatorPosition: state.setIndicatorPosition,
      openDrawer: state.openDrawer,
    }),
    shallow,
  );

  const isInvalidPlayer = !!player?.selection_status && player.selection_status.status !== 'valid';

  const onPlayerChange = () => {
    setIndicatorPosition({position, index, substitute: isSubstitute});
    openDrawer();
  };

  const onPlayerInfoClick = () => {
    setPlayerStatsModal({context: 'lineup-player', player: player!, action: 'remove', isSubstitute});
    mixpanel?.track('Button Clicked', {button: 'competition.show_player_info'});
  };

  return (
    <Flex
      className="player"
      data-selected={selected}
      flex={1}
      pos="relative"
      flexDir="column"
      justify="space-between"
      align="center"
      gap="2%"
      w="10"
      maxW="30%"
      {...props}
    >
      <Avatar
        className="player-avatar"
        pos="relative"
        bg="white"
        p="1.5"
        w="auto"
        h="55%"
        shadow="xs"
        sx={{aspectRatio: '1/1'}}
        borderWidth={selected ? (isInvalidPlayer ? 1 : 2) : 0}
        borderColor="black"
        _after={{
          content: '""',
          display: isInvalidPlayer ? 'initial' : 'none',
          pos: 'absolute',
          inset: 0,
          border: '2px solid',
          borderColor: 'red.600',
          rounded: '50%',
        }}
        data-selected={selected}
        src={player?.image?.thumb ?? player?.image?.original}
        icon={<TShirtIcon filled={false} width="80%" height="auto" />}
        cursor="pointer"
        onClick={onPlayerChange}
      >
        {!player && isEditable && (
          <Center
            rounded="full"
            bg="green.400"
            p="0.5"
            w="30%"
            pos="absolute"
            insetEnd="20%"
            bottom="15%"
            sx={{aspectRatio: '1/1'}}
          >
            <AddIcon flex={1} w="full" h="auto" color="white" />
          </Center>
        )}

        {isInvalidPlayer ? (
          <PlayerWarningIndicator player={player.common_name} />
        ) : player?.is_captain || player?.is_vice_captain ? (
          <CaptainIndicator status={player.is_captain ? 'captain' : 'vice-captain'} />
        ) : null}
      </Avatar>

      <Flex
        className="player-info"
        bg={!isSubstitute ? (!player ? 'white' : selected ? 'black' : 'green.500') : 'white'}
        flex={1}
        maxH="40%"
        align="center"
        justify="center"
        gap="0.5"
        w="full"
        minH="7"
        p="0.5"
        fontSize={['max(2vw, 0.55rem)', null, 'min(1.5vw, 0.75rem)']}
        lineHeight="1.2"
        fontWeight="700"
        textAlign="center"
        rounded="0.25rem"
        onClick={player ? () => onPlayerInfoClick?.() : onPlayerChange}
        cursor="pointer"
      >
        {player ? (
          <PlayerInfo player={player} substitute={isSubstitute} />
        ) : (
          <Text fontWeight="extrabold" color="green.500" userSelect="none">
            {isEditable ? t('app.actions.add') : '—'}
          </Text>
        )}

        {player?.scored_fantasy_points !== undefined && (
          <Box
            bg={player.is_substitute ? 'white' : 'gray.600'}
            pos="absolute"
            top="35%"
            insetStart="0"
            rounded="md"
            shadow="xs"
            minW="4ch"
            maxW="6ch"
            textAlign="center"
            textOverflow="clip"
          >
            <Text px="0.5" fontSize="clamp(0.5rem, 2.5vw, 0.75rem)" userSelect="none">
              {format(player?.scored_fantasy_points)}
            </Text>
          </Box>
        )}

        {player?.is_substituted && (
          <Box pos="absolute" top="35%" insetEnd={[0, 2]}>
            <ArrowDownIcon scale={!player.is_substitute ? 1 : -1} transform="auto" />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

type PlayerInfoProps = {
  player: Player;
  substitute?: boolean;
};

const PlayerInfo = ({player: {common_name}, substitute}: PlayerInfoProps) => (
  <>
    {/* {team.logo?.thumb || team.logo?.original ? (
      <Image
        py="0.5"
        ps="3%"
        w="2.4em"
        maxH="full"
        className="player-info-team-logo"
        src={team.logo.thumb ?? team.logo.original}
        alt={team.name}
      />
    ) : (
      <Center className="player-info-team-code" h="full" px="1" bg="gray.800">
        <Text color="white">{team.code}</Text>
      </Center>
    )} */}

    <Center
      as="p"
      dir="auto"
      title={common_name}
      className="player-info-name"
      noOfLines={{base: 2, xl: 3}}
      w="full"
      maxH="full"
      fontSize="inherit"
      fontWeight="700"
      color={substitute ? 'black' : 'white'}
      sx={{hyphens: 'auto'}}
    >
      {common_name}
    </Center>
  </>
);
